import {useState, useEffect} from 'react';
import {Form} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import moment from 'moment';
import {
  CREATE_QUICK_ACTION,
  UPDATE_QUICK_ACTION,
  GET_QUICK_ACTION_BY_ID,
  GET_ALL_QUICK_ACTIONS_CATEGORIES,
} from '../resolvers';
import {TRANSLATE_MULTIPLE_FIELDS} from 'graphql/queries/investorUpdates';
import {UploadDocumentToBucket} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
import {isDev} from 'utils/constants';

const bucket = isDev
  ? 'dabafinancialinstruments-nonprod'
  : 'dabafinancialinstruments-prod';

export const useQuickActionForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const {id} = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = !!id;
  const categoryIdFromQuery = searchParams.get('categoryId');

  // Watch form fields
  const name = Form.useWatch('name', form) || '';
  const language = Form.useWatch('language', form) || 'EN';
  const selectedTranslationLanguage = Form.useWatch('translation', form) || '';

  // Translation state (not part of form fields)
  const [translatedActions, setTranslatedActions] = useState([]);
  const [enableChangeTranslation, setEnableChangeTranslation] = useState(true);
  const [uploadingIcon, setUploadingIcon] = useState(false);

  // Fetch categories for dropdown
  const {data: categoriesData, loading: loadingCategories} = useQuery(
    GET_ALL_QUICK_ACTIONS_CATEGORIES,
    {
      variables: {
        activeOnly: false,
      },
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const categories =
    categoriesData?.getAllQuickActionCategories?.__typename ===
    'ResponseWithQuickActionCategories'
      ? categoriesData?.getAllQuickActionCategories?.data || []
      : [];

  const [createAction, {loading: creating}] = useMutation(CREATE_QUICK_ACTION, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const [updateAction, {loading: updating}] = useMutation(UPDATE_QUICK_ACTION, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const [translateText, {loading: translating}] = useMutation(
    TRANSLATE_MULTIPLE_FIELDS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const {data, loading: loadingAction} = useQuery(GET_QUICK_ACTION_BY_ID, {
    variables: {quickActionId: id},
    skip: !isEditMode,
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  useEffect(() => {
    if (
      isEditMode &&
      data?.getQuickActionById?.__typename === 'ResponseWithQuickAction'
    ) {
      const action = data?.getQuickActionById?.data;
      if (action) {
        const translations = action.translations || [];
        const defaultTranslationLanguage =
          translations.length > 0 ? translations[0].language : '';

        form.setFieldsValue({
          name: action.name || '',
          key: action.key || '',
          language: action.language || 'EN',
          category: action.category?.id || '',
          icon: action.icon || '',
          isActive: action.isActive ?? true,
          isDefault: action.isDefault ?? false,
          newUntil: action.newUntil ? moment(action.newUntil) : null,
          translation: defaultTranslationLanguage,
        });

        if (translations.length > 0) {
          setTranslatedActions(
            translations.map(trans => ({
              name: trans.name,
              language: trans.language,
            })),
          );
        }
      }
    } else if (!isEditMode && categoryIdFromQuery) {
      // Pre-fill category when creating from category actions tab
      form.setFieldsValue({
        category: categoryIdFromQuery,
      });
    }
  }, [data, isEditMode, form, categoryIdFromQuery]);

  const handleIconUpload = async file => {
    try {
      setUploadingIcon(true);
      const fileLink = await UploadDocumentToBucket(
        file,
        () => {},
        bucket,
        true,
        'quick-actions',
      );

      if (fileLink) {
        form.setFieldsValue({icon: fileLink});
        toast.success('Icon uploaded successfully');
      } else {
        toast.error('Icon upload failed');
      }
    } catch (error) {
      toast.error('Icon upload failed');
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleAddTranslation = () => {
    if (!selectedTranslationLanguage) {
      toast.error('Please select a translation language first');
      return;
    }

    const languageExists = translatedActions.some(
      trans => trans.language === selectedTranslationLanguage,
    );

    if (languageExists) {
      toast.error('Translation for this language already exists');
      return;
    }

    const newTranslation = {
      name: '',
      language: selectedTranslationLanguage,
    };

    setTranslatedActions([...translatedActions, newTranslation]);
    form.setFieldsValue({translation: ''}); // Clear selection
  };

  const handleTranslate = async () => {
    if (!selectedTranslationLanguage) {
      toast.error('Please select a translation language first');
      return;
    }

    if (!name) {
      toast.error('Please enter a name to translate');
      return;
    }

    try {
      const {data: translateData} = await translateText({
        variables: {
          translations: [
            {
              sourceLang: language,
              sourceText: name,
              targetLang: selectedTranslationLanguage,
            },
          ],
        },
      });

      if (translateData?.translateText?.__typename === 'Error') {
        toast.error(translateData.translateText.message);
        return;
      }

      const translatedName = translateData?.translateText?.translations?.[0];

      if (translatedName) {
        const newTranslation = {
          name: translatedName,
          language: selectedTranslationLanguage,
        };

        // We only want one translation at a time as per user request
        setTranslatedActions([newTranslation]);
        form.setFieldsValue({translation: ''});
        toast.success('Translated successfully');
      }
    } catch (error) {
      toast.error('Translation failed');
    }
  };

  const handleChangeTranslatedValues = (key, event, name) => {
    let eventName = name;
    let eventValue = name ? event : event.target.value;
    const newFormValues = [...translatedActions];
    newFormValues[key][eventName] = eventValue;
    setTranslatedActions(newFormValues);
  };

  const handleRemoveTranslation = index => {
    const newFormValues = translatedActions.filter((_, i) => i !== index);
    setTranslatedActions(newFormValues);
  };

  const handleSubmit = values => {
    const input = {
      name: values.name,
      key: values.key,
      language: values.language,
      icon: values.icon,
      isActive: values.isActive ?? true,
      isDefault: values.isDefault,
      ...(values.category && {category: values.category}),
      ...(values.newUntil && {
        newUntil: values.newUntil.toISOString(),
      }),
      ...(translatedActions.length > 0 && {
        translations: translatedActions.map(trans => ({
          name: trans.name,
          language: trans.language,
        })),
      }),
    };

    if (isEditMode) {
      updateAction({
        variables: {
          quickActionId: id,
          input,
        },
      })
        .then(({data: {updateQuickAction}}) => {
          if (updateQuickAction.__typename === 'Error') {
            toast.error(updateQuickAction.message);
          } else {
            toast.success(updateQuickAction.message);
            // Navigate back to category edit if we came from there
            if (categoryIdFromQuery) {
              navigate(
                `/dashboard/quick-actions/categories/${categoryIdFromQuery}/edit`,
              );
            } else {
              navigate('/dashboard/quick-actions/actions');
            }
          }
        })
        .catch(error => toast.error(error.message));
    } else {
      createAction({
        variables: {
          input,
        },
      })
        .then(({data: {createQuickAction}}) => {
          if (createQuickAction.__typename === 'Error') {
            toast.error(createQuickAction.message);
          } else {
            toast.success(createQuickAction.message);
            // Navigate back to category edit if we came from there
            if (categoryIdFromQuery) {
              navigate(
                `/dashboard/quick-actions/categories/${categoryIdFromQuery}/edit`,
              );
            } else {
              navigate('/dashboard/quick-actions/actions');
            }
          }
        })
        .catch(error => toast.error(error.message));
    }
  };

  // Get action name from query data or form (for dynamic updates)
  const actionNameFromData =
    isEditMode &&
    data?.getQuickActionById?.__typename === 'ResponseWithQuickAction'
      ? data?.getQuickActionById?.data?.name
      : null;
  const actionNameFromForm = Form.useWatch('name', form) || '';
  const actionName = isEditMode
    ? actionNameFromForm || actionNameFromData
    : null;

  return {
    form,
    name,
    language,
    selectedTranslationLanguage,
    translatedActions,
    setTranslatedActions,
    enableChangeTranslation,
    setEnableChangeTranslation,
    handleAddTranslation,
    handleChangeTranslatedValues,
    handleRemoveTranslation,
    handleIconUpload,
    handleTranslate,
    handleSubmit,
    loading: creating || updating || translating,
    loadingAction,
    loadingCategories,
    uploadingIcon,
    translating,
    isEditMode,
    categories,
    actionName,
  };
};
